// frontend/src/pages/MemberLoanStatusPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './MemberLoanStatusPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MemberLoanStatusPage = () => {
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [loanStatus, setLoanStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const response = await fetch(`${API_URL}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        // Ensure each member item has both id (doc id) and memberId (business id)
        const list = (data.data.members || []).map(m => ({
          id: m.id || m._id || m.docId || m.id,
          memberId: m.memberId || m.memberNo || m.member_id || (m.personalInfo && m.personalInfo.memberId) || '',
          personalInfo: m.personalInfo || {}
        }));
        setMembers(list);
      } else {
        setError(data.message || 'Failed to load members');
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      setError('Failed to load members');
    }
  };

  const fetchLoanStatus = async (memberId) => {
    if (!memberId) return;

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const response = await fetch(`${API_URL}/members/${encodeURIComponent(memberId)}/loan-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 404) {
        setLoanStatus(null);
        setError(`No member found for identifier: ${memberId}`);
        setIsLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        // Defensive defaults so UI won't crash if some fields missing
        const payload = data.data || {};
        const safeStatus = {
          memberInfo: payload.memberInfo || {
            memberId: memberId,
            memberName: 'Unknown',
            email: '',
            phone: ''
          },
          activeLoans: payload.activeLoans || [],
          completedLoans: payload.completedLoans || [],
          summary: payload.summary || {
            totalActiveLoans: 0,
            totalCompletedLoans: 0,
            totalCurrentBalance: 0,
            totalMonthlyDeduction: 0,
            memberShares: 0,
            memberSavings: 0
          }
        };
        setLoanStatus(safeStatus);
      } else {
        setLoanStatus(null);
        setError(data.message || 'Failed to fetch loan status');
      }
    } catch (err) {
      console.error('Error:', err);
      setLoanStatus(null);
      setError('Failed to fetch loan status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMemberSelect = (e) => {
    const memberId = e.target.value;
    setSelectedMemberId(memberId);
    if (memberId) {
      fetchLoanStatus(memberId);
    } else {
      setLoanStatus(null);
    }
  };

  const getLoanTypeColor = (loanType) => {
    switch (loanType) {
      case 'normal': return '#3498db';
      case 'school': return '#e67e22';
      case 'emergency': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    // support Firestore Timestamp objects and plain date strings
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return 'N/A';
    }
  };

  const safeNumber = (v) => {
    if (v === undefined || v === null || isNaN(Number(v))) return 0;
    return Number(v);
  };

  return (
    <AdminLayout
      activePage="member-loan-status"
      pageTitle="Member Loan Status"
      breadcrumbs={[
        { label: 'Dashboard', link: '/admin/dashboard' },
        { label: 'Members', link: '/admin/members' },
        { label: 'Loan Status' }
      ]}
    >
      <div className="page-content">
        <div className="card">
          <div className="form-group">
            <label>Select Member *</label>
            <select
              value={selectedMemberId}
              onChange={handleMemberSelect}
              disabled={isLoading}
            >
              <option value="">-- Select Member to View Loan Status --</option>
              {members.map(member => (
                <option key={member.memberId || member.id} value={member.memberId || member.id}>
                  {(member.memberId || member.id)} - {member.personalInfo?.fullName || 'Unknown'}
                </option>
              ))}
            </select>
            <button className="btn-link" onClick={fetchMembers} style={{marginLeft:10}}>🔄 Refresh</button>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {isLoading && (
          <div className="card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>⏳ Loading loan status...</p>
            </div>
          </div>
        )}

        {loanStatus && !isLoading && (
          <>
            {/* Member Info Card */}
            <div className="card card-blue">
              <div className="card-header">
                <h2>👤 {loanStatus.memberInfo.memberName}</h2>
              </div>
              <div className="member-info-grid">
                <div>
                  <strong>Member ID:</strong> {loanStatus.memberInfo.memberId}
                </div>
                <div>
                  <strong>Email:</strong> {loanStatus.memberInfo.email || 'N/A'}
                </div>
                <div>
                  <strong>Phone:</strong> {loanStatus.memberInfo.phone || 'N/A'}
                </div>
                <div>
                  <strong>Total Shares:</strong> KES {safeNumber(loanStatus.summary.memberShares).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Summary Card */}
            <div className="card card-orange">
              <div className="card-header">
                <h3>📊 Loan Summary</h3>
              </div>
              <div className="summary-grid-large">
                <div className="summary-item">
                  <div className="summary-label">Active Loans</div>
                  <div className="summary-value">{safeNumber(loanStatus.summary.totalActiveLoans)}</div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Total Balance</div>
                  <div className="summary-value" style={{ color: '#e74c3c' }}>
                    KES {safeNumber(loanStatus.summary.totalCurrentBalance).toLocaleString()}
                  </div>
                </div>
                <div className="summary-item highlight">
                  <div className="summary-label">Monthly Deduction</div>
                  <div className="summary-value" style={{ color: '#c0392b', fontSize: '1.8em' }}>
                    KES {Math.ceil(safeNumber(loanStatus.summary.totalMonthlyDeduction)).toLocaleString()}
                  </div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Completed Loans</div>
                  <div className="summary-value" style={{ color: '#27ae60' }}>
                    {safeNumber(loanStatus.summary.totalCompletedLoans)}
                  </div>
                </div>
              </div>
            </div>

            {/* Active Loans */}
            {loanStatus.activeLoans.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3>💳 Active Loans ({loanStatus.activeLoans.length})</h3>
                </div>

                <div className="loans-container">
                  {loanStatus.activeLoans.map((loan, index) => (
                    <div key={index} className="loan-card" style={{ borderLeft: `5px solid ${getLoanTypeColor(loan.loanType)}` }}>
                      <div className="loan-header">
                        <div>
                          <h4>{loan.loanId}</h4>
                          <span
                            className="loan-type-badge"
                            style={{ backgroundColor: getLoanTypeColor(loan.loanType) }}
                          >
                            {loan.loanType ? loan.loanType.toUpperCase() : 'N/A'}
                          </span>
                          {loan.isHistoricalImport && (
                            <span className="badge-historical">📜 Historical</span>
                          )}
                        </div>
                        <div className="loan-percentage">
                          <div className="percentage-circle">
                            {safeNumber(loan.percentagePaid)}%
                          </div>
                          <small>Paid</small>
                        </div>
                      </div>

                      <div className="loan-details-grid">
                        <div>
                          <label>Original Amount</label>
                          <div className="detail-value">KES {safeNumber(loan.originalAmount).toLocaleString()}</div>
                        </div>
                        <div>
                          <label>Current Balance</label>
                          <div className="detail-value" style={{ color: '#e74c3c' }}>
                            KES {Math.ceil(safeNumber(loan.currentBalance)).toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <label>Start Date</label>
                          <div className="detail-value">{formatDate(loan.disbursementDate)}</div>
                        </div>
                        <div>
                          <label>Remaining Months</label>
                          <div className="detail-value">{safeNumber(loan.remainingMonths)} months</div>
                        </div>
                      </div>

                      <div className="loan-payment-breakdown">
                        <h5>Monthly Payment Breakdown:</h5>
                        <div className="payment-grid">
                          <div>
                            <label>Principal</label>
                            <div className="payment-value">
                              KES {Math.ceil(safeNumber(loan.monthlyPrincipalPayment)).toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <label>Interest (1%)</label>
                            <div className="payment-value">
                              KES {Math.ceil(safeNumber(loan.monthlyInterestPayment)).toLocaleString()}
                            </div>
                          </div>
                          <div className="payment-total">
                            <label>Total Monthly</label>
                            <div className="payment-value-total">
                              KES {Math.ceil(safeNumber(loan.totalMonthlyDeduction)).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="loan-progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${Math.min(100, safeNumber(loan.percentagePaid))}%`,
                            backgroundColor: getLoanTypeColor(loan.loanType)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Loans History */}
            {loanStatus.completedLoans.length > 0 && (
              <div className="card card-green">
                <div className="card-header">
                  <h3>✅ Completed Loans History ({loanStatus.completedLoans.length})</h3>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Loan ID</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Total Interest</th>
                        <th>Start Date</th>
                        <th>Completion Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanStatus.completedLoans.map((loan, index) => (
                        <tr key={index}>
                          <td>
                            {loan.loanId}
                            {loan.isHistoricalImport && (
                              <span className="badge-historical" style={{ marginLeft: '5px', fontSize: '0.7em' }}>
                                📜
                              </span>
                            )}
                          </td>
                          <td>
                            <span
                              className="loan-type-badge-small"
                              style={{ backgroundColor: getLoanTypeColor(loan.loanType) }}
                            >
                              {loan.loanType}
                            </span>
                          </td>
                          <td>KES {safeNumber(loan.originalAmount).toLocaleString()}</td>
                          <td>KES {Math.ceil(safeNumber(loan.totalInterest)).toLocaleString()}</td>
                          <td>{formatDate(loan.disbursementDate)}</td>
                          <td>{formatDate(loan.completionDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* No Active Loans */}
            {loanStatus.activeLoans.length === 0 && (
              <div className="card">
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ fontSize: '3em' }}>✅</p>
                  <h3>No Active Loans</h3>
                  <p>This member has no active loans at the moment.</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="card">
              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/admin/members/${encodeURIComponent(selectedMemberId)}/statement`)}
                >
                  📄 View Full Statement
                </button>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/admin/loans/import-historical')}
                >
                  📥 Import More Loans
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default MemberLoanStatusPage;
